'use client'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useUser } from '@/contexts/UserContext'
import '@/app/dashboard.css'

const storeColorCount = 10

function getInitials(name: string): string {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return String(name || '').substring(0, 2).toUpperCase()
}

export function StoreSelector() {
  const router = useRouter()
  const { user, refreshUser } = useUser()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [switchingStore, setSwitchingStore] = useState(false)
  const [adminStoreNavScope, setAdminStoreNavScope] = useState(false)

  useEffect(() => {
    setAdminStoreNavScope(localStorage.getItem('admin_store_nav_scope') === '1')
  }, [])

  const firmStores = useMemo(() => {
    return ((user as any)?.firmStores || []).map((store: any, index: number) => ({
      id: store.id,
      firm_id: store.firm_id,
      name: store.name,
      code: store.code,
      initials: getInitials(store.name || store.code),
      colorIndex: index % storeColorCount,
    }))
  }, [user])

  const selectedStore = firmStores.find((store: any) => Number(store.id) === Number(user?.firm_store_id))
  const canSwitchStore = firmStores.length > 1

  useEffect(() => {
    if (!user) return
    if (selectedStore) return
    if (!user.firm_id && firmStores.length === 0) return
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    router.replace('/login?reason=no_store')
  }, [firmStores.length, router, selectedStore, user])

  const filteredStores = firmStores.filter((store: any) =>
    String(store.name || '').toLowerCase().includes(search.toLowerCase()) ||
    String(store.code || '').toLowerCase().includes(search.toLowerCase())
  )

  const handleSwitchStore = async (store: any) => {
    setSwitchingStore(true)
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch('https://app.canerozen.com/api/auth/switch-store', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: store.id }),
      })
      if (response.ok) {
        await refreshUser()
        router.push('/')
      }
    } catch (error) {
      console.error('Store switch failed:', error)
    } finally {
      setSwitchingStore(false)
      setOpen(false)
      setSearch('')
    }
  }

  const handleAdminStoreNavScope = async () => {
    const next = !adminStoreNavScope
    setAdminStoreNavScope(next)
    localStorage.setItem('admin_store_nav_scope', next ? '1' : '0')
    await refreshUser()
  }

  if (!user || firmStores.length === 0) return null

  return (
    <div className="dashboard-selector">
      <button
        type="button"
        onClick={() => canSwitchStore && setOpen(!open)}
        className="dashboard-selector-button"
        disabled={switchingStore}
      >
        {selectedStore ? (
          <>
            <div className={`dashboard-avatar dashboard-avatar--c${selectedStore.colorIndex}`}>
              {selectedStore.initials}
            </div>
            <div className="dashboard-info">
              <div className="dashboard-name">{selectedStore.name}</div>
              <div className="dashboard-code">{selectedStore.name} ({selectedStore.code})</div>
            </div>
          </>
        ) : (
          <div className="dashboard-info">
            <div className="dashboard-name">Mağaza Seçin</div>
          </div>
        )}
        {canSwitchStore && <i className="fa-duotone fa-chevron-down dashboard-chevron" />}
      </button>

      {open && canSwitchStore && (
        <>
          <div
            className="dashboard-dropdown-overlay"
            onClick={() => {
              setOpen(false)
              setSearch('')
            }}
          />
          <div className="dashboard-dropdown">
            <div className="dashboard-dropdown-header">
              <div className="dashboard-dropdown-top">
                <div className="dashboard-dropdown-copy">
                  <div className="dashboard-dropdown-title">Mağaza Seç</div>
                  <div className="dashboard-dropdown-subtitle">Seçili mağaza panel bağlamını belirler.</div>
                </div>
                {Number(user?.admin || 0) === 1 && (
                  <button
                    type="button"
                    className={`dashboard-scope-toggle ${adminStoreNavScope ? 'dashboard-scope-toggle--active' : ''}`}
                    onClick={handleAdminStoreNavScope}
                  >
                    <span className="dashboard-scope-toggle__track">
                      <span className="dashboard-scope-toggle__thumb" />
                    </span>
                    <span>Mağaza yetkileri</span>
                  </button>
                )}
              </div>
              <div className="dashboard-search">
                <label className="dashboard-search-label" htmlFor="layout-store-search">
                  <i className="fa-duotone fa-search dashboard-search-icon" />
                  <span>Mağaza ara</span>
                </label>
                <input
                  id="layout-store-search"
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="dashboard-search-input"
                  autoFocus
                />
              </div>
            </div>

            <div className="dashboard-dropdown-list">
              {filteredStores.map((store: any) => (
                <button
                  key={store.id}
                  type="button"
                  onClick={() => handleSwitchStore(store)}
                  disabled={switchingStore}
                  className={`dashboard-item ${selectedStore?.id === store.id ? 'active' : ''}`}
                >
                  <div className={`dashboard-item-avatar dashboard-avatar--c${store.colorIndex}`}>
                    {store.initials}
                  </div>
                  <div className="dashboard-item-info">
                    <div className="dashboard-item-name">{store.name}</div>
                    <div className="dashboard-item-code">{store.code}</div>
                  </div>
                </button>
              ))}

              {filteredStores.length === 0 && (
                <div className="dashboard-dropdown-empty">Sonuç bulunamadı</div>
              )}
            </div>

            <div className="dashboard-dropdown-footer">
              <span>{firmStores.length} mağaza</span>
              {Number(user?.admin || 0) === 1 && adminStoreNavScope && (
                <strong>Yetkili nav görünümü açık</strong>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
